import { useEffect, useState } from 'react';
import { useChatStore } from '../store/chat-store';
import type { VipProgress } from '@contracts/eligibility';

interface Props {
  serverUrl: string;
  token: string;
  /** Called when the server reports vipStatus='vip' (poll catches the unlock). */
  onUnlocked: () => void;
}

const POLL_INTERVAL_MS = 5_000;

export function LockedState({ serverUrl, token, onUnlocked }: Props) {
  const initial = useChatStore(s => s.eligibility.progress);
  const [progress, setProgress] = useState<VipProgress | null>(initial);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch(`${serverUrl.replace(/\/$/, '')}/api/v1/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const data = (await r.json()) as VipProgress;
        if (cancelled) return;
        setProgress(data);
        useChatStore.getState().setEligibility(data.vipStatus !== 'vip', data);
        if (data.vipStatus === 'vip') {
          onUnlocked();
        }
      } catch {
        /* poll quietly */
      }
    };
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [serverUrl, token, onUnlocked]);

  const cfg = progress?.thresholds;
  const depositPct = Math.round(((progress?.progress.deposit ?? 0) * 100));
  const wagerPct = Math.round(((progress?.progress.wager ?? 0) * 100));
  const remDeposit = progress?.remaining.deposit ?? cfg?.depositThreshold ?? 0;
  const remWager = progress?.remaining.wager ?? cfg?.wagerThreshold ?? 0;
  const fmt = (n: number) => formatCurrency(n, cfg?.currency ?? 'INR');

  return (
    <div className="flex-1 flex items-center justify-center p-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 text-amber-700 flex items-center justify-center shadow-sm">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </div>
        </div>

        <h2 className="text-center text-2xl font-bold text-gray-900 tracking-tight">
          VIP Chat is locked
        </h2>
        <p className="text-center text-sm text-gray-500 mt-1.5 mb-6">
          Reach the eligibility threshold to unlock the chat. Updates live.
        </p>

        {cfg?.depositEnabled && (
          <ProgressRow
            label="Deposits"
            pct={depositPct}
            description={remDeposit > 0
              ? `Deposit ${fmt(remDeposit)} more to unlock`
              : 'Deposit threshold met'}
            current={progress?.totals.deposit ?? 0}
            target={cfg.depositThreshold}
            currency={cfg.currency}
          />
        )}
        {cfg?.wagerEnabled && (
          <ProgressRow
            label="Wagering"
            pct={wagerPct}
            description={remWager > 0
              ? `Wager ${fmt(remWager)} more to unlock`
              : 'Wagering threshold met'}
            current={progress?.totals.wager ?? 0}
            target={cfg.wagerThreshold}
            currency={cfg.currency}
          />
        )}
        {cfg?.depositEnabled && cfg?.wagerEnabled && (
          <p className="text-center text-[11px] text-gray-400 mt-2">
            {cfg.logic === 'OR' ? 'Either condition unlocks the chat.' : 'Both conditions are required.'}
          </p>
        )}

        <div className="mt-6 rounded-xl bg-white border border-gray-200 p-3.5 text-center">
          <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">Status</div>
          <div className="mt-1 flex items-center justify-center gap-1.5 text-sm text-gray-700">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            Waiting for activity. Auto-checks every 5 seconds.
          </div>
        </div>
      </div>
    </div>
  );
}

function ProgressRow(props: { label: string; pct: number; description: string; current: number; target: number; currency: string }) {
  const pct = Math.max(0, Math.min(100, props.pct));
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-700">{props.label}</span>
        <span className="text-[11px] text-gray-500 tabular-nums">
          {formatCurrency(props.current, props.currency)} / {formatCurrency(props.target, props.currency)}
        </span>
      </div>
      <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="mt-1 text-[11px] text-gray-500">{props.description} · {pct}%</div>
    </div>
  );
}

function formatCurrency(n: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString('en-IN')}`;
  }
}
